import {
  type AsyncUnzipOptions,
  type AsyncZipOptions,
  type FlateError,
  type Unzipped,
  unzip as ffUnzip,
  zip as ffZip,
} from 'fflate';

/**
 * Async version of fflate.unzip
 *
 * Asynchronously decompresses a ZIP archive
 * @param data The raw compressed ZIP file
 * @param opts The ZIP extraction options
 * @returns The decompressed files
 */
export async function unzipAsync(
  data: Uint8Array,
  opts?: AsyncUnzipOptions & {
    signal?: AbortSignal;
  },
): Promise<Unzipped> {
  return new Promise((resolve, reject) => {
    const abort = ffUnzip(
      data,
      opts ?? {},
      (err: FlateError | null, data: Unzipped) => {
        if (err) {
          reject(err);
        } else {
          resolve(data);
        }
      },
    );

    if (opts?.signal) {
      opts.signal.addEventListener('abort', abort);
    }
  });
}

/**
 * Async version of fflate.zip
 *
 * Asynchronously creates a ZIP file
 * @param data The directory structure for the ZIP archive
 * @param opts The main options, merged with per-file options
 * @returns The compressed ZIP file
 */
export async function zipAsync(
  data: Unzipped,
  opts?: AsyncZipOptions & {
    signal?: AbortSignal;
  },
): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const abort = ffZip(
      data,
      opts ?? {},
      (err: FlateError | null, data: Uint8Array) => {
        if (err) {
          reject(err);
        } else {
          resolve(data);
        }
      },
    );

    if (opts?.signal) {
      opts.signal.addEventListener('abort', abort);
    }
  });
}

/**
 * Resolves a path relative to the base directory.
 * @param baseDir The base directory
 * @param path The path to resolve
 * @returns The resolved path
 */
export function resolvePath(baseDir: string, path: string): string {
  if (path.startsWith('/')) {
    return resolvePath(path, '');
  }

  const stack = [];
  for (const p of [...baseDir.split('/'), ...path.split('/')]) {
    if (p === '..') {
      stack.pop();
    } else if (p !== '.' && p !== '') {
      stack.push(p);
    }
  }

  return stack.join('/');
}

/**
 * Resolves a path's directory, assuming it is a file.
 * @param path The path to resolve
 * @returns The directory of the path
 */
export function resolveDir(path: string): string {
  return path.split('/').slice(0, -1).join('/');
}
